import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, TextField, Button, IconButton, Stack } from '@mui/material';
import { Timeline } from 'rsuite';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Cookies from 'js-cookie';
import api from '../../../../../services/api';
import './Timeline.css';

// Componente que carrega imagens e arquivos de comentários — tenta endpoint autenticado,
// com fallback para URL direta do NGINX caso o backend ainda não tenha o endpoint.
const AttachmentImage = ({ caminho }) => {
    const [src, setSrc] = React.useState(null);
    const [erro, setErro] = React.useState(false);

    // Identificar se é uma imagem pelo formato
    const formatMatch = caminho.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    const isImage = !!formatMatch;

    // Constrói a URL de fallback usando EXATAMENTE a baseURL já configurada no Axios
    const baseURL = api.defaults.baseURL || '';
    const baseClean = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
    const urlDireta = caminho.replace('/home/wwgrup/', `${baseClean}/`);

    React.useEffect(() => {
        let cancelled = false;
        let objectUrl = null;

        const tryApi = async () => {
            try {
                const res = await api.get(
                    `v1/protected/ordemservico/comentario/midia?caminho=${encodeURIComponent(caminho)}`,
                    { responseType: 'blob' }
                );
                
                if (!cancelled) {
                    objectUrl = URL.createObjectURL(res.data);
                    setSrc(objectUrl);
                }
            } catch {
                // Fallback: usa URL direta servida pelo NGINX
                if (!cancelled) setSrc(urlDireta);
            }
        };

        tryApi();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [caminho]); // eslint-disable-line react-hooks/exhaustive-deps

    if (erro) return <Typography variant="caption" color="error">❌ Anexo não disponível</Typography>;
    if (!src) return <Typography variant="caption" color="textSecondary">⏳ Carregando anexo...</Typography>;
    
    if (!isImage) {
        return (
            <Button 
                variant="outlined" 
                size="small" 
                color="primary" 
                onClick={() => window.open(urlDireta, '_blank')}
                sx={{ mt: 1, textTransform: 'none' }}
                startIcon={<AttachFileIcon />}
            >
                Abrir / Baixar Anexo
            </Button>
        );
    }

    return (
        <Box
            component="img"
            src={src}
            alt="Anexo"
            onError={() => setErro(true)}
            onClick={() => window.open(urlDireta, '_blank')}
            sx={{
                mt: 1, maxWidth: '100%', maxHeight: 200, borderRadius: 1,
                border: '1px solid #ccc', cursor: 'pointer', display: 'block',
                '&:hover': { opacity: 0.85, transform: 'scale(1.01)', transition: '0.2s' }
            }}
        />
    );
};

const LogInterativo = ({ ordemId, darkMode, themeHelper }) => {
    const [currentComments, setCurrentComments] = useState([]);
    const [newMsg, setNewMsg] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [isPostingComment, setIsPostingComment] = useState(false);

    const fetchComments = async () => {
        try {
            const token = Cookies.get('token');
            const response = await api.get(`/v1/protected/ordemservico/comentario/listar/${ordemId}`, {
                headers: { 'Authorization': 'Bearer ' + token }
            });
            setCurrentComments(response.data);
        } catch (err) {
            console.error("Erro ao buscar comentários:", err);
        }
    };

    useEffect(() => {
        if (ordemId) {
            fetchComments();
        }
    }, [ordemId]);

    const handlePostComment = async () => {
        if (!newMsg && !selectedFile) return;
        setIsPostingComment(true);
        try {
            const token = Cookies.get('token');
            const idUsuario = Cookies.get('id_usuario');
            const formData = new FormData();
            formData.append('id_ordem_servico', ordemId);
            formData.append('id_usuario', idUsuario);
            formData.append('mensagem', newMsg);
            if (selectedFile) formData.append('foto', selectedFile);

            await api.post('/v1/protected/ordemservico/comentario/gravar', formData, {
                headers: { 'Authorization': 'Bearer ' + token }
            });
            setNewMsg('');
            setSelectedFile(null);
            fetchComments();
        } catch (err) {
            console.error("Erro ao postar comentário:", err);
        } finally {
            setIsPostingComment(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        let date;
        if (Array.isArray(dateString)) {
            const [year, month, day, hour = 0, minute = 0, second = 0] = dateString;
            date = new Date(year, month - 1, day, hour, minute, second);
        } else if (typeof dateString === 'string') {
            date = new Date(dateString.replace(' ', 'T'));
        } else {
            date = new Date(dateString);
        }
        if (!date || isNaN(date.getTime())) return '—';
        return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <Card sx={{ height: '100%', backgroundColor: darkMode ? '#333' : '#fff', borderRadius: 3, display: 'flex', flexDirection: 'column', border: `1px solid ${themeHelper.border}` }}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${themeHelper.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryToggleOffIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">Log Interativo</Typography>
            </Box>
            <Box sx={{ flexGrow: 1, p: 2, overflowY: 'auto', maxHeight: '400px', minHeight: '300px' }}>
                {currentComments.length > 0 ? (
                    <Timeline align="left">
                        {currentComments.map((comment, idx) => (
                            <Timeline.Item key={idx} dot={<AccountCircleIcon sx={{ fontSize: 24, color: 'primary.main' }} />}>
                                <Box sx={{ mb: 1.5, p: 1, borderRadius: 1, backgroundColor: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            {comment.usuario?.nome_amigavel || 'Usuário'}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            · {formatDate(comment.data_hora)}
                                        </Typography>
                                    </Box>
                                    {comment.mensagem && (
                                        <Typography variant="body2" sx={{ color: themeHelper.text }}>
                                            {comment.mensagem}
                                        </Typography>
                                    )}
                                    {comment.caminho_foto && (
                                        <Box sx={{ mt: 1, p: 1, borderRadius: 1, border: '1px dashed #aaa', backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
                                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                                                📎 Anexo enviado por <b>{comment.usuario?.nome_amigavel || 'Usuário'}</b> em {formatDate(comment.data_hora)}
                                            </Typography>
                                            <AttachmentImage caminho={comment.caminho_foto} />
                                        </Box>
                                    )}
                                </Box>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                ) : (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
                        <Typography variant="body2">Nenhum comentário ainda.</Typography>
                    </Box>
                )}
            </Box>
            <Box sx={{ p: 2, borderTop: `1px solid ${themeHelper.border}`, backgroundColor: darkMode ? '#2a2a2a' : '#f9f9f9' }}>
                <Stack spacing={1}>
                    <TextField fullWidth size="small" placeholder="Escreva uma nota..." value={newMsg} onChange={(e) => setNewMsg(e.target.value)} multiline rows={2} variant="outlined" />
                    {selectedFile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: 1, border: '1px solid #aaa', backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
                            <ComputerOutlinedIcon fontSize="small" color="primary" />
                            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 'bold' }}>{selectedFile.name}</Typography>
                                <Typography variant="caption" color="textSecondary">{(selectedFile.size / 1024).toFixed(1)} KB</Typography>
                            </Box>
                            <IconButton size="small" onClick={() => setSelectedFile(null)}>
                                <HighlightOffIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button component="label" size="small" startIcon={<ComputerOutlinedIcon />} sx={{ textTransform: 'none' }}>
                            {selectedFile ? 'Trocar arquivo' : 'Anexar arquivo'}
                            <input type="file" hidden onChange={(e) => setSelectedFile(e.target.files[0])} />
                        </Button>
                        <Button variant="contained" size="small" color="primary" disabled={isPostingComment || (!newMsg && !selectedFile)} onClick={handlePostComment}>
                            {isPostingComment ? '...' : 'Postar'}
                        </Button>
                    </Box>
                </Stack>
            </Box>
        </Card>
    );
};

export default LogInterativo;
